import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  PHONE_COUNTRIES,
  DEFAULT_PHONE_COUNTRY,
  resolvePhoneCountry,
  buildE164,
  type PhoneCountry
} from "../phone/countryData";

import { FormFieldLabel } from "./FormFieldLabel";
import { SelectionModal } from "./SelectionModal";

interface PhoneFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  helperText?: string;
  error?: string | null;
  className?: string;
}

export function PhoneField({
  id,
  value,
  onChange,
  disabled,
  label,
  required,
  helperText,
  error,
  className
}: PhoneFieldProps) {
  const { t } = useTranslation();
  const { country, localDigits } = useMemo(
    () => resolvePhoneCountry(value, DEFAULT_PHONE_COUNTRY),
    [value]
  );
  const inputId = id ?? "phone-field-input";
  const [isPickerOpen, setPickerOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);

  const closePicker = useCallback(
    (focusTarget: "trigger" | "input" | null = "trigger") => {
      setPickerOpen(false);
      setTimeout(() => {
        if (focusTarget === "trigger" && triggerRef.current) {
          triggerRef.current.focus();
        }
        if (focusTarget === "input" && inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    },
    []
  );

  const openPicker = () => {
    if (disabled) {
      return;
    }
    setPickerOpen(true);
  };

  const handleNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/[^0-9]/g, "");
    if (!digits) {
      onChange(country.dialCode);
      return;
    }
    onChange(buildE164(country, digits));
  };

  const handleCountrySelect = (next: PhoneCountry) => {
    if (!localDigits) {
      onChange(next.dialCode);
    } else {
      onChange(buildE164(next, localDigits));
    }
    closePicker("input");
  };

  useEffect(() => {
    if (isPickerOpen && firstItemRef.current) {
      firstItemRef.current.focus();
    }
  }, [isPickerOpen]);

  const wrapperClasses = [
    "relative flex rounded-xl shadow-sm ring-1 ring-inset ring-slate-200 focus-within:ring-2 focus-within:ring-inset focus-within:ring-sky-600 dark:ring-slate-700",
    disabled ? "bg-slate-50 text-slate-500 ring-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:ring-slate-700" : "bg-white dark:bg-slate-900",
    className
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label className="block" htmlFor={inputId}>
          <FormFieldLabel label={label} optional={!required} />
        </label>
      )}
      <div className={wrapperClasses}>
        <button
          type="button"
          ref={triggerRef}
          className="flex items-center gap-2 rounded-l-xl border-r border-slate-200 px-3 py-2.5 text-slate-500 hover:bg-slate-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
          onClick={openPicker}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={isPickerOpen}
        >
          <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-slate-100 object-cover shadow-sm dark:bg-slate-800" aria-hidden="true">
            {country.flag}
          </span>
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{country.dialCode}</span>
        </button>
        <input
          id={inputId}
          ref={inputRef}
          type="tel"
          inputMode="tel"
          pattern="[0-9]*"
          className="block w-full min-w-0 flex-1 rounded-r-xl border-0 bg-transparent py-2.5 pl-3 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:bg-slate-50 dark:text-slate-100 dark:placeholder-slate-500 dark:disabled:bg-slate-800"
          value={localDigits}
          onChange={handleNumberChange}
          placeholder={t("forms.phone.placeholder")}
          disabled={disabled}
          required={required}
        />
      </div>
      {isPickerOpen && (
        <SelectionModal
          title={t("forms.phone.country_picker_title")}
          closeLabel={t("actions.close")}
          onClose={() => closePicker()}
        >
          <ul className="flex flex-col divide-y divide-slate-100 dark:divide-slate-800">
            {PHONE_COUNTRIES.map((item, index) => {
              const isActive = item.code === country.code;
              const optionClasses = [
                "flex w-full items-center justify-between px-4 py-3 text-left transition hover:bg-slate-50 active:bg-slate-100 dark:hover:bg-slate-800 dark:active:bg-slate-700",
                isActive ? "bg-sky-50 hover:bg-sky-50 text-sky-900 dark:bg-sky-900/20 dark:hover:bg-sky-900/30 dark:text-sky-300" : "text-slate-900 dark:text-slate-100"
              ].filter(Boolean).join(" ");

              return (
                <li key={item.code} className="block">
                  <button
                    type="button"
                    ref={index === 0 ? firstItemRef : undefined}
                    className={optionClasses}
                    onClick={() => handleCountrySelect(item)}
                    aria-current={isActive}
                  >
                    <span className="flex items-center gap-3 overflow-hidden">
                      <span className="flex h-4 w-6 shrink-0 overflow-hidden rounded-sm bg-slate-100 object-cover shadow-sm dark:bg-slate-800" aria-hidden="true">
                        {item.flag}
                      </span>
                      <span className="truncate text-sm font-medium">
                        {item.label}
                      </span>
                    </span>
                    <span className="ml-auto pl-3 text-sm text-slate-500 font-mono dark:text-slate-400">
                      {item.dialCode}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </SelectionModal>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{helperText}</p>
      )}
      {error && <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{error}</p>}
    </div>
  );
}
